import React, { useMemo } from "react";
import { Doughnut } from "react-chartjs-2";
import { useFetchList } from "../../hooks/useFetchList";
import { ensureChartSetup } from "./ChartSetup";
import { inferFirstKey } from "../../api";

ensureChartSetup();

const palette = [
  "#4CAF50", // Green
  "#9C27B0", // Purple
  "#FF9800", // Orange
  "#00BCD4", // Cyan
  "#F44336", // Red
  "#2196F3", // Blue
  "#FF5722", // Deep Orange
  "#607D8B", // Blue Grey
  "#795548", // Brown
  "#E91E63", // Pink
  "#673AB7", // Deep Purple
  "#3F51B5", // Indigo
  "#009688", // Teal
  "#8BC34A", // Light Green
  "#CDDC39", // Lime
  "#FFEB3B", // Yellow
  "#FFC107", // Amber
  "#FF6F00", // Orange Accent
  "#E65100", // Deep Orange Accent
  "#BF360C", // Red Accent
  "#D32F2F", // Red Darken
  "#C2185B", // Pink Darken
  "#7B1FA2", // Purple Darken
  "#512DA8", // Deep Purple Darken
  "#303F9F", // Indigo Darken
  "#1976D2", // Blue Darken
  "#0288D1", // Light Blue Darken
  "#0097A7", // Cyan Darken
  "#00796B", // Teal Darken
  "#388E3C", // Green Darken
  "#689F38", // Light Green Darken
  "#827717", // Lime Darken
  "#F57F17", // Yellow Darken
  "#FF8F00", // Amber Darken
  "#E65100", // Orange Darken
  "#5D4037", // Brown Darken
  "#455A64", // Blue Grey Darken
  "#37474F", // Blue Grey Dark
  "#263238", // Blue Grey Darker
];

export default function SalesByCategoryChart({ headers = {} }) {
  const { data, loading, error } = useFetchList("/v1/frontend/salesByCategory", {
    headers,
  });

  const chart = useMemo(() => {
    if (!data || data.length === 0) return null;
    const keys = Object.keys(data[0] || {});
    const categoryKey = inferFirstKey(keys, [/(category|name)$/i]);
    const valueKey = inferFirstKey(
      keys.filter((k) => k !== categoryKey),
      [/(sales|amount|total|count)/i]
    );
    const labels = data.map((r) => r[categoryKey]);
    const values = data.map((r) => Number(r[valueKey]) || 0);
    return {
      labels,
      datasets: [
        {
          label: "Sales",
          data: values,
          backgroundColor: labels.map((_, i) => palette[i % palette.length]),
        },
      ],
    };
  }, [data]);

  if (loading) return <div style={{ textAlign: "center" }}>Loading...</div>;
  if (error) return <div style={{ color: "#d33" }}>Failed to load.</div>;
  if (!chart) return <div>No data.</div>;

  return (
    <div
      style={{
        width: "100%",
        minHeight: 220,
        height: "30vw",
        maxHeight: 340,
        boxSizing: "border-box",
      }}
    >
      <Doughnut
        data={chart}
        options={{
          plugins: {
            legend: { position: "bottom", labels: { font: { size: 12 } } },
            tooltip: {
              callbacks: {
                label: function (context) {
                  const label = context.label || "";
                  const value = context.parsed || 0;
                  const formattedValue = new Intl.NumberFormat("en-US", {
                    style: "currency",
                    currency: "USD",
                    minimumFractionDigits: 0,
                    maximumFractionDigits: 0,
                  }).format(value);
                  return `${label}: ${formattedValue}`;
                },
              },
            },
          },
          maintainAspectRatio: false,
        }}
      />
    </div>
  );
}
